import {
  Box,
  Code,
  Divider,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { dateTimeTransformer } from 'helpers/dateTransformer';
import useConfigQuery from 'hooks/useConfigQuery';
import useUserQuery from 'hooks/useUserQuery';
import { LocalazyMenu } from 'layout/dev-tools/LocalazyMenu';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuSearchCode } from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import config from '../config';

export default function DevTools() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const configQuery = useConfigQuery();
  const userQuery = useUserQuery();

  const [lastSelectedLanguage, setLastSelectedLanguage] = useState(() => i18n.language);
  const [devtoolsEnabled, setDevtoolsEnabled] = useLocalStorage('devtoolsEnabled', false);

  const languages = useMemo(() => {
    const languagesWithDuplicates = Object.values(configQuery.data?.languages ?? {}).flat();
    return [...new Set(languagesWithDuplicates)].map((lang) => {
      const upper = lang.toUpperCase();
      return { label: upper, value: lang.toLowerCase() };
    });
  }, [configQuery.data?.languages]);

  // Update local storage based on URL param
  useEffect(() => {
    if (searchParams.get('devtools') === 'true') {
      setDevtoolsEnabled(true);
    }
    if (searchParams.get('devtools') === 'false') {
      setDevtoolsEnabled(false);
    }
  }, [searchParams, setDevtoolsEnabled]);

  // Ctrl+Shift+D toggles devtools
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setDevtoolsEnabled((prev) => !prev);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setDevtoolsEnabled]);

  if (!devtoolsEnabled) {
    return null;
  }

  const offset = 18;
  const localOffsetY = 52;

  return (
    <Box position="fixed" zIndex="10000" bottom={import.meta.env.MODE === 'development' ? `${localOffsetY + offset}px` : `${offset}px`} right="3">
      <Popover placement="top-end" closeOnBlur={false} closeOnEsc={false}>
        <PopoverTrigger>
          <IconButton
            size="lg"
            rounded="full"
            aria-label="Open DevTools"
            bg="gray.800"
            color="gray.100"
            _hover={{ bg: 'gray.700' }}
            icon={<Icon as={LuSearchCode} />}
          />
        </PopoverTrigger>
        <PopoverContent minW="300px" bg="gray.800" color="gray.100" borderColor="gray.600">
          <PopoverBody>
            <FormLabel fontSize="sm" color="gray.300" mb={1}>
              {'Language'}
            </FormLabel>
            <Select
              size="sm"
              value={i18n.language === 'cimode' ? lastSelectedLanguage : i18n.language}
              onChange={(e) => {
                const newLanguage = e.target.value || userQuery.data?.language?.toLowerCase() || 'en';
                setLastSelectedLanguage(newLanguage);
                if (i18n.language !== 'cimode') {
                  i18n.changeLanguage(newLanguage);
                }
              }}
              bg="gray.700"
              borderColor="gray.600"
              color="gray.100"
            >
              {languages.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Stack direction="row" align="center" py={4}>
              <FormControl display="flex" gap="2">
                <Switch
                  isChecked={i18n.language === 'cimode'}
                  onChange={() => (i18n.language === 'cimode' ? i18n.changeLanguage(lastSelectedLanguage) : i18n.changeLanguage('cimode'))}
                  id="show-translation-keys"
                  colorScheme="blue"
                />
                <FormLabel htmlFor="show-translation-keys" mb={0} color="gray.200">
                  {'Show translation keys'}
                </FormLabel>
              </FormControl>
            </Stack>
            <Divider borderColor="gray.600" />
            <LocalazyMenu locale={lastSelectedLanguage} />
            <Divider borderColor="gray.600" />
            <Stack pt={4} spacing={1}>
              {config.buildTag && (
                <Code display="block" variant="subtle" bg="gray.700" color="gray.200">
                  {`Tag: ${config.buildTag}`}
                </Code>
              )}
              {config.buildBranch && (
                <Code display="block" variant="subtle" bg="gray.700" color="gray.200">
                  {`Branch: ${config.buildBranch}`}
                </Code>
              )}
              <Code display="block" variant="subtle" bg="gray.700" color="gray.200">
                {`Commit: ${config.buildCommit}`}
              </Code>
              <Code display="block" variant="subtle" bg="gray.700" color="gray.200">
                {`Time: ${config.buildTime !== '-' ? dateTimeTransformer(config.buildTime) : '-'}`}
              </Code>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
}
